import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faQuestionCircle, faClock } from '@fortawesome/free-solid-svg-icons';

const TestConfiguration = ({ config, onChange, isSubscribed, theme }) => {
  const questionOptions = [5, 10, 25, 50, 100];
  const timeOptions = [5, 10, 20, 30, 60];

  const styles = {
    container: {
      background: theme?.background?.card,
      borderRadius: '1.25rem',
      padding: '1.5rem',
      border: `1px solid ${theme?.primary?.gray?.[300]}`,
      boxShadow: theme?.shadows?.base,
    },
    section: {
      marginBottom: '2rem',
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      marginBottom: '1.25rem',
    },
    icon: {
      fontSize: '1.25rem',
      color: theme?.primary?.orange?.[500],
    },
    title: {
      fontSize: '1.125rem',
      fontWeight: '600',
      color: theme?.primary?.gray?.[900],
    },
    optionsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
      gap: '0.75rem',
    },
    optionButton: {
      position: 'relative',
      height: '3.5rem',
      borderRadius: '0.75rem',
      border: `1px solid ${theme?.primary?.gray?.[200]}`,
      background: theme?.primary?.gray?.[50],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      overflow: 'hidden',
    },
    optionButtonActive: {
      background: `linear-gradient(135deg, ${theme?.primary?.orange?.[500]} 0%, ${theme?.primary?.orange?.[600]} 100%)`,
      border: `1px solid ${theme?.primary?.orange?.[600]}`,
      color: 'white',
      boxShadow: `0 4px 12px ${theme?.primary?.orange?.[500]}30`,
    },
    optionButtonLocked: {
      opacity: 0.8,
      cursor: 'pointer',
    },
    optionValue: {
      fontSize: '1.125rem',
      fontWeight: '600',
    },
    lockIcon: {
      position: 'absolute',
      top: '0.25rem',
      right: '0.25rem',
      fontSize: '0.75rem',
      color: theme?.primary?.gray?.[500],
    },
    divider: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      margin: '1.5rem 0',
    },
    dividerLine: {
      flex: 1,
      height: '1px',
      background: theme?.primary?.gray?.[200],
    },
    dividerText: {
      fontSize: '0.875rem',
      color: theme?.primary?.gray?.[500],
      fontWeight: '500',
    },
  };

  const handleQuestionSelect = (value) => {
    onChange({
      ...config,
      numberOfQuestions: value,
      timeAmount: 0,
      mode: 'questions'
    });
  };

  const handleTimeSelect = (value) => {
    onChange({
      ...config,
      numberOfQuestions: 0,
      timeAmount: value,
      mode: 'time'
    });
  };

  return (
    <motion.div
      style={styles.container}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
    >
      {/* Questions Section */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <FontAwesomeIcon icon={faQuestionCircle} style={styles.icon} />
          <h3 style={styles.title}>Number of Questions</h3>
        </div>
        
        <div style={styles.optionsGrid}>
          {questionOptions.map((value) => {
            const isActive = config.mode === 'questions' && config.numberOfQuestions === value;
            const isLocked = !isSubscribed && value > 5;
            
            return (
              <motion.button
                key={value}
                style={{
                  ...styles.optionButton,
                  ...(isActive && styles.optionButtonActive),
                  ...(isLocked && styles.optionButtonLocked),
                }}
                onClick={() => handleQuestionSelect(value)}
                whileHover={{ scale: 1.05, boxShadow: theme?.shadows?.md }}
                whileTap={{ scale: 0.95 }}
              >
                <span style={{
                  ...styles.optionValue,
                  color: isActive ? 'white' : theme?.primary?.gray?.[900]
                }}>
                  {value}
                </span>
                {isLocked && (
                  <FontAwesomeIcon icon={faLock} style={styles.lockIcon} />
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
      
      {/* Divider */}
      <div style={styles.divider}>
        <div style={styles.dividerLine} />
        <span style={styles.dividerText}>OR</span>
        <div style={styles.dividerLine} />
      </div>
      
      {/* Time Section */}
      <div style={{ ...styles.section, marginBottom: 0 }}>
        <div style={styles.sectionHeader}>
          <FontAwesomeIcon icon={faClock} style={styles.icon} />
          <h3 style={styles.title}>Time Duration (minutes)</h3>
        </div>
        
        <div style={styles.optionsGrid}>
          {timeOptions.map((value) => {
            const isActive = config.mode === 'time' && config.timeAmount === value;
            const isLocked = !isSubscribed;
            
            return (
              <motion.button
                key={value}
                style={{
                  ...styles.optionButton,
                  ...(isActive && styles.optionButtonActive),
                  ...(isLocked && styles.optionButtonLocked),
                }}
                onClick={() => handleTimeSelect(value)}
                whileHover={{ scale: 1.05, boxShadow: theme?.shadows?.md }}
                whileTap={{ scale: 0.95 }}
              >
                <span style={{
                  ...styles.optionValue,
                  color: isActive ? 'white' : theme?.primary?.gray?.[900]
                }}>
                  {value}
                </span>
                {isLocked && (
                  <FontAwesomeIcon icon={faLock} style={styles.lockIcon} />
                )}
              </motion.button>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

export default TestConfiguration;